<template>
  <el-container class="chat-page">
    <!-- 搜索框 -->
    <el-header height="60px" class="search-container">
      <el-input v-model="searchText" placeholder="搜索聊天记录..." prefix-icon="el-icon-search" clearable
        class="search-input" />
    </el-header>

    <!-- 会话列表 -->
    <el-scrollbar class="chat-list-container" style="height: 100%">
      <!-- AI对话分组 -->
      <div class="chat-group">
        <div class="group-header collapsible-header" @click="aiSessionsCollapsed = !aiSessionsCollapsed">
          <div class="header-left">
            <i class="el-icon-arrow-right collapse-icon" :class="{ expanded: !aiSessionsCollapsed }"></i>
            <span class="group-title">🤖 AI对话</span>
          </div>
          <span class="group-count">({{ aiSessions.length }})</span>
        </div>

        <el-collapse-transition>
          <div v-show="!aiSessionsCollapsed" class="chat-list">
            <!-- 加载状态 -->
            <div v-if="loadingAI && aiSessions.length === 0" class="loading-placeholder">
              <i class="el-icon-loading"></i>
              <span>正在加载AI会话...</span>
            </div>
            
            <!-- AI会话列表 -->
            <template v-else>
              <template v-for="(session, index) in aiSessions">
                <chat-item v-if="session && session.headImage && session.showName" :key="`ai-session-${session.id}-${index}`" :chat="session"
                  :index="index" :loading="loading" :disableDoubleClick="true" :disableContextMenu="true" @active="onActiveAIItem" class="ai-chat-item" :class="{
                    active: activeAISession?.id === session.id,
                    'has-complex-tools': session.hasComplexTools,
                    thinking: session.isThinking,
                    'roocode-task': session.isRooCodeTask,
                  }" />
              </template>
              
              <!-- 空状态提示 -->
              <div v-if="!loadingAI && aiSessions.length === 0" class="empty-placeholder">
                <i class="el-icon-chat-dot-round"></i>
                <span>暂无AI对话记录</span>
                <div class="empty-hint">使用 Roo Code 创建AI对话后，将在此处显示</div>
              </div>
            </template>
          </div>
        </el-collapse-transition>
      </div>

      <!-- 人类IM会话分组 - 融合显示（私聊+群聊+终端收件箱） -->
      <div class="chat-group">
        <div class="group-header">
          <div class="header-left">
            <i class="spacer-icon"></i>
            <span class="group-title">💬 聊天消息</span>
          </div>
          <span class="group-count">({{ filteredHumanSessions.length }})</span>
        </div>

        <div class="chat-list">
          <div v-for="(chat, index) in filteredHumanSessions" :key="generateChatKey(chat)">
            <chat-item v-show="!chat.delete && chat.showName" :chat="enrichChatDisplay(chat)" :index="index"
              @click.native="onActiveHumanItem(chat, index)" @delete="onDelHumanItem(chat, index)"
              @top="onTopHumanItem(chat, index)" :active="isHumanChatActive(chat)" :class="{
                'terminal-inbox': chat.isTerminalInbox,
                'normal-chat': !chat.isTerminalInbox
              }"></chat-item>
          </div>
        </div>
      </div>
    </el-scrollbar>

  </el-container>
</template>

<script>
import ChatItem from "../components/chat/ChatItem.vue";
import TerminalSelector from "../components/chat/TerminalSelector.vue";
import { log } from "@/utils/logManager.js";

export default {
  name: "chat",
  components: {
    ChatItem,
    TerminalSelector,
  },
  data() {
    return {
      searchText: "",
      messageContent: "",
      group: {},
      groupMembers: [],

      // AI对话相关
      aiSessions: [],
      activeAISession: null,
      loadingAI: false,

      // 统一会话管理器
      sessionEventUnsubscribe: null,

      // 加载状态和重试
      retryCount: 0,
      maxRetries: 3,
      loadingErrors: [],

      // 🔧 新增：防止循环激活的状态管理
      isActivatingSession: false,           // 激活状态锁
      lastActivatedSessionId: null,         // 最后激活的会话ID
      activationDebounceTimer: null,        // 防抖计时器
      activationCooldown: 300,              // 激活冷却时间（毫秒）

      // 防重复加载状态
      isLoadingAllSessions: false,          // 防止重复加载所有会话

      // 调试模式开关，避免过多日志
      debugMode: false,

      // RooCode任务监听器
      rooCodeTaskListener: null,            // RooCode任务历史变更监听器
      rooCodeRefreshTimer: null,            // 定时刷新计时器
      
      // 折叠状态
      aiSessionsCollapsed: false,           // AI对话列表折叠状态
    };
  },

  watch: {
    // 监听折叠状态变化，自动保存
    aiSessionsCollapsed(newVal) {
      this.saveCollapseState();
    },

    // 监听 chatStore.chats 变化，数据加载完成后同步到 void
    'chatStore.chats': {
      handler(newChats, oldChats) {
        const oldLength = oldChats ? oldChats.length : 0;
        const newLength = newChats ? newChats.length : 0;

        // 同步所有变化：初始加载(0->N)、新增会话(N->N+1)、删除会话(N->N-1)
        if (newLength !== oldLength && newLength > 0) {
          this.syncSessionsToVoid(newChats);
        }
      },
      immediate: false,
      deep: false
    }
  },
  computed: {
    chatStore() {
      return this.$store.state.chatStore;
    },
    loading() {
      return (
        this.chatStore.loadingGroupMsg ||
        this.chatStore.loadingPrivateMsg ||
        this.loadingAI
      );
    },

    // 过滤后的AI对话
    filteredAiSessions() {
      // 🔧 移除死循环：不在计算属性中触发数据加载
      if (this.aiSessions.length === 0) {
        return [];
      }

      log.debug(
        "InboxSession",
        "过滤AI对话，原始数量:",
        this.aiSessions.length
      );
      log.debug("InboxSession", "搜索文本:", this.searchText);

      log.debug(
        "InboxSession",
        "过滤AI对话，原始数量:",
        this.aiSessions.length
      );
      log.debug("InboxSession", "搜索文本:", this.searchText);

      const filtered = this.aiSessions
        .filter((session) => {
          if (!session) {
            log.warn("InboxSession", "发现空的session对象");
            return false;
          }

          if (!session.showName) {
            log.warn("InboxSession", "session缺少showName字段:", session);
            // 🔧 不要因为缺少showName就丢弃，尝试使用其他字段
            session.showName = session.title || session.name || session.id || `AI对话 ${Date.now()}`;
            console.log("[InboxSession] 🔧 已自动修复showName:", session.showName);
          }

          const match = session.showName
            .toLowerCase()
            .includes(this.searchText.toLowerCase());
          return match;
        })
        .map((session, index) => {
          log.debug("InboxSession", `验证AI会话 ${index}:`, session.showName);

          // 优化：一次性生成AI会话摘要，避免重复调用
          const aiSummary = session.lastContent || session.lastMessage || this.generateAISessionSummary(session);

          // 最严格的字段确保
          const verifiedSession = {
            ...session,
            // 核心必需字段 - 绝对不能为空
            headImage: session.headImage || session.avatar || "🤖",
            showName: session.showName || `AI对话 ${index + 1}`,
            lastContent: aiSummary,
            lastSendTime:
              session.lastSendTime || session.lastMessageTime || Date.now(),
            type: session.type || "ai",
            targetId:
              session.targetId || session.id || `ai_${Date.now()}_${index}`,

            // 聊天相关必需字段
            unreadCount: session.unreadCount || 0,
            messages: session.messages || [],
            sendNickName: session.sendNickName || "",
            atMe: Boolean(session.atMe),
            atAll: Boolean(session.atAll),

            // AI特有字段
            aiModel: session.aiModel || "Claude-3.5-Sonnet",
            messageCount: session.messageCount || 0,
            hasComplexTools: Boolean(session.hasComplexTools),
            isActive: Boolean(session.isActive),
            isThinking: Boolean(session.isThinking),

            // 确保其他字段
            id: session.id || `ai_${Date.now()}_${index}`,
            aiThreadId: session.aiThreadId || session.id,
            avatar: session.avatar || session.headImage || "🤖",
            lastMessage: aiSummary, // 复用已生成的摘要
            lastMessageTime:
              session.lastMessageTime || session.lastSendTime || Date.now(),
            lastModified: session.lastModified || new Date().toISOString(),
            createdAt: session.createdAt || new Date().toISOString(),
            delete: false,
            stored: true,
          };

          // 最终强制验证 - 这些字段绝对不能为空或undefined
          const criticalFields = [
            "headImage",
            "showName",
            "lastContent",
            "lastSendTime",
            "type",
            "targetId",
          ];
          criticalFields.forEach((field) => {
            if (!verifiedSession[field]) {
              log.error(
                "InboxSession",
                `严重错误：AI对话 ${index} 字段 ${field} 为空，强制设置默认值`
              );
              switch (field) {
                case "headImage":
                  verifiedSession.headImage = "🤖";
                  break;
                case "showName":
                  verifiedSession.showName = `AI对话 ${index + 1}`;
                  break;
                case "lastContent":
                  verifiedSession.lastContent = this.generateAISessionSummary(verifiedSession);
                  break;
                case "lastSendTime":
                  verifiedSession.lastSendTime = Date.now();
                  break;
                case "type":
                  verifiedSession.type = "ai";
                  break;
                case "targetId":
                  verifiedSession.targetId = `ai_emergency_${Date.now()}_${index}`;
                  break;
              }
            }
          });

          return verifiedSession;
        });

      log.debug("InboxSession", "过滤后AI会话数量:", filtered.length);

      // 最最终验证
      filtered.forEach((session, index) => {
        if (!session.headImage) {
          log.error(
            "InboxSession",
            `致命错误：过滤后的AI对话 ${index} 仍然没有headImage!`,
            session
          );
          // 紧急修复
          session.headImage = "🤖";
        }
        if (!session.showName) {
          log.error(
            "InboxSession",
            `致命错误：过滤后的AI对话 ${index} 仍然没有showName!`,
            session
          );
          // 紧急修复
          session.showName = `AI对话 ${index + 1}`;
        }
      });

      // 🔧 按最后会话时间倒序排列（最新的在第一位）
      const sortedFiltered = filtered.sort((a, b) => {
        // 获取最后会话时间，优先使用 lastSendTime，其次 lastMessageTime，最后 lastModified
        const getLastTime = (session) => {
          const timeFields = [
            session.lastSendTime,
            session.lastMessageTime,
            session.lastModified ? new Date(session.lastModified).getTime() : null,
            session.createdAt ? new Date(session.createdAt).getTime() : null
          ].filter(time => time != null);

          return timeFields.length > 0 ? Math.max(...timeFields) : 0;
        };

        const timeA = getLastTime(a);
        const timeB = getLastTime(b);

        // 倒序排列：时间越大（越新）排在前面
        return timeB - timeA;
      });

      log.debug("InboxSession", "AI会话按时间倒序排序完成，最新会话:", sortedFiltered[0]?.showName);

      return sortedFiltered;
    },

    // 🔧 修正：融合的人类IM会话（支持目标终端模式）
    filteredHumanSessions() {
      const searchTextLower = this.searchText.toLowerCase();

      // 🔧 快速预过滤，避免复杂检查
      const validChats = (this.chatStore.chats || []).filter(chat => {
        if (!chat || chat.delete) return false;

        // 🔧 修正：支持新的终端会话格式
        if (chat.isTerminalChat || chat.isTerminalInbox) {
          // 终端会话：检查显示名称
          return chat.showName && chat.showName.toLowerCase().includes(searchTextLower);
        }

        // 普通会话：排除AI会话，检查显示名称
        return chat.showName &&
          chat.type !== 'AI' &&
          chat.showName.toLowerCase().includes(searchTextLower);
      });

      // 🔧 简化去重逻辑，使用Map提高性能
      const uniqueChatsMap = new Map();

      validChats.forEach(chat => {
        let key;

        // 🔧 修正：根据会话类型生成不同的键
        if (chat.isTerminalChat) {
          key = `terminal-${chat.senderTerminal}-${chat.targetId}`;
        } else if (chat.isTerminalInbox) {
          key = `inbox-${chat.receivingTerminal}-${chat.targetId}`;
        } else {
          key = `normal-${chat.type}-${chat.targetId}`;
        }

        // 保留最新的会话（lastSendTime最大的）
        if (!uniqueChatsMap.has(key) ||
          (chat.lastSendTime > uniqueChatsMap.get(key).lastSendTime)) {
          uniqueChatsMap.set(key, chat);
        }
      });

      // 🔧 优化排序：减少复杂时间计算
      const uniqueChats = Array.from(uniqueChatsMap.values());

      uniqueChats.sort((a, b) => {
        // 🔧 修正：优先显示终端会话
        const aIsTerminal = a.isTerminalChat || a.isTerminalInbox;
        const bIsTerminal = b.isTerminalChat || b.isTerminalInbox;

        if (aIsTerminal && !bIsTerminal) return -1;
        if (!aIsTerminal && bIsTerminal) return 1;

        // 时间排序
        return (b.lastSendTime || 0) - (a.lastSendTime || 0);
      });

      console.log(`🔧 [InboxSession] 过滤后的人类会话数量: ${uniqueChats.length}`);
      console.log(`🔧 [InboxSession] 终端会话数量: ${uniqueChats.filter(c => c.isTerminalChat || c.isTerminalInbox).length}`);
      console.log(`🔧 [InboxSession] 普通会话数量: ${uniqueChats.filter(c => !c.isTerminalChat && !c.isTerminalInbox).length}`);

      return uniqueChats;
    },
  },

  async mounted() {
    console.log("[InboxSession] 组件挂载，开始初始化...");

    // 恢复折叠状态
    this.loadCollapseState();

    // 🔧 修复死循环：在mounted中初始化AI会话数据
    try {
      await this.loadAllSessions();
      console.log("[InboxSession] 初始化完成");
    } catch (error) {
      console.error("[InboxSession] 初始化失败:", error);
    }

    // 监听RooCode任务历史变更事件
    this.setupRooCodeTaskListener();
  },

  // ✅ 添加 activated 钩子处理 keep-alive 激活
  async activated() {
    console.log("[InboxSession] 组件被激活（从keep-alive中恢复）");

    // 🔧 修复：避免重复初始化，只在真正需要时才重新加载数据
    const hasHumanChats = this.chatStore?.chats?.length > 0;
    const hasAIChats = this.aiSessions?.length > 0;

    // 🔧 只有在数据完全为空且没有正在加载时才重新加载
    if (!hasHumanChats && !hasAIChats && !this.isLoadingAllSessions) {
      console.log("[InboxSession] 检测到数据为空，重新加载数据...");
      await this.loadAllSessions();
    } else {
      console.log("[InboxSession] 数据已存在或正在加载中，跳过重新加载");
    }

    console.log("[InboxSession] 组件激活处理完成");
  },

  beforeDestroy() {
    this.cleanupEventListeners();
    
    // 清理RooCode任务监听器
    this.cleanupRooCodeTaskListener();

    // 清理缓存，防止内存泄漏
    if (this._aiSummaryCache) {
      this._aiSummaryCache.clear();
      this._aiSummaryCache = null;
    }
  },



  methods: {
    // 加载折叠状态
    loadCollapseState() {
      try {
        const saved = localStorage.getItem('inboxSession_aiCollapsed');
        if (saved !== null) {
          this.aiSessionsCollapsed = JSON.parse(saved);
          console.log("[InboxSession] 已恢复AI会话折叠状态:", this.aiSessionsCollapsed);
        }
      } catch (error) {
        console.warn("[InboxSession] 加载折叠状态失败:", error);
      }
    },

    // 保存折叠状态
    saveCollapseState() {
      try {
        localStorage.setItem('inboxSession_aiCollapsed', JSON.stringify(this.aiSessionsCollapsed));
        console.log("[InboxSession] 已保存AI会话折叠状态:", this.aiSessionsCollapsed);
      } catch (error) {
        console.warn("[InboxSession] 保存折叠状态失败:", error);
      }
    },

    // 🔧 修正：组件初始化逻辑
    async initializeComponent() {
      console.log("[InboxSession] 🔧 开始初始化组件...");

      // 🔧 使用requestIdleCallback延迟初始化，避免阻塞主线程
      requestIdleCallback(async () => {
        try {
          // 🔧 修正：调用正确的终端会话初始化方法
          await this.loadAllSessions();

          console.log("[InboxSession] ✅ 组件初始化完成");
        } catch (error) {
          console.error("[InboxSession] ❌ 组件初始化失败:", error);
        }
      });
    },

    // 🔧 修正：加载所有会话数据（防重复加载）
    async loadAllSessions() {
      // 🔧 防重复加载检查
      if (this.isLoadingAllSessions) {
        console.log("[InboxSession] 🔧 正在加载中，跳过重复请求");
        return;
      }

      this.isLoadingAllSessions = true;
      console.log("[InboxSession] 🔧 开始加载所有会话数据...");

      try {
        // 🔧 并行加载人类聊天和AI对话数据
        const [humanChatsPromise, aiChatsPromise] = await Promise.allSettled([
          this.loadHumanChats(),
          this.loadAISessionsFromUnified()
        ]);

        if (humanChatsPromise.status === 'fulfilled') {
          console.log("[InboxSession] ✅ 人类聊天数据加载完成");
        } else {
          console.error("[InboxSession] ❌ 人类聊天数据加载失败:", humanChatsPromise.reason);
        }

        if (aiChatsPromise.status === 'fulfilled') {
          console.log("[InboxSession] ✅ AI对话数据加载完成");
        } else {
          console.error("[InboxSession] ❌ AI对话数据加载失败:", aiChatsPromise.reason);
        }

        console.log("[InboxSession] 🔧 所有会话数据加载完成");
      } catch (error) {
        console.error("[InboxSession] ❌ 加载会话数据失败:", error);
      } finally {
        this.isLoadingAllSessions = false;
      }
    },

    // 统一处理：加载人类聊天数据
    async loadHumanChats() {
      // 数据已存在于chatStore中，无需特殊处理
    },

    // 同步会话到 void 的 unifiedSessionService
    syncSessionsToVoid(humanSessions) {
      try {
        if (!humanSessions || humanSessions.length === 0 || !window.vscodeServices) {
          return;
        }

        const unifiedSessionService = window.vscodeServices.get('IUnifiedSessionService');
        if (!unifiedSessionService || !unifiedSessionService.syncVueSessionsFromWebView) {
          return;
        }

        // 转换为 void 期望的格式（type: 'vue-im'）
        const formattedSessions = humanSessions.map(session => ({
          ...session,
          type: 'vue-im',
          id: session.targetId || session.id,
          sessionId: session.targetId || session.id,
          showName: session.showName || session.name || '未知会话',
          lastSendTime: session.lastSendTime || session.lastMessageTime || Date.now(),
          lastMessageTime: session.lastSendTime || session.lastMessageTime || Date.now(),
          messageCount: session.messages?.length || 0
        }));

        unifiedSessionService.syncVueSessionsFromWebView(formattedSessions);
      } catch (error) {
        // 同步失败静默处理
      }
    },

    // 获取默认头像
    getDefaultAvatar(type) {
      switch (type) {
        case "GROUP":
          return "👥";
        case "PRIVATE":
          return "👤";
        case "ai":
          return "🤖";
        default:
          return "💬";
      }
    },

    // 🌟 IM会话激活 - 只调用统一服务（带防抖和循环保护）
    async onActiveItem(index) {
      console.log("[InboxSession] 🔧 激活IM会话 - 原始索引:", index);

      // 🔧 注意：这里的index应该是chatStore.chats原始数组的索引，不是过滤后的索引
      const session = this.chatStore.chats[index];
      if (!session) {
        console.error("[InboxSession] IM会话不存在，原始索引:", index);
        console.log("[InboxSession] 🔧 当前chats数量:", this.chatStore.chats?.length || 0);
        return;
      }

      console.log("[InboxSession] 🔧 找到会话:", session.showName, "targetId:", session.targetId);

      // 🔧 生成统一会话ID - 统一使用vue-im前缀
      const sessionRef = session.targetId || session.id;
      const unifiedSessionId = `vue-im-${sessionRef}`;

      // 🔧 防循环检查：如果正在激活相同会话，直接返回
      if (this.isActivatingSession && this.lastActivatedSessionId === unifiedSessionId) {
        console.log("[Vue] 🛡️ 检测到重复激活，忽略:", unifiedSessionId);
        return;
      }

      // 🔧 清除之前的防抖计时器
      if (this.activationDebounceTimer) {
        clearTimeout(this.activationDebounceTimer);
      }

      // 🔧 设置防抖计时器
      this.activationDebounceTimer = setTimeout(async () => {
        try {
          // 🔧 设置激活状态锁
          this.isActivatingSession = true;
          this.lastActivatedSessionId = unifiedSessionId;

          console.log("[Vue] 🎯 用户选择IM会话:", session.showName, "ID:", unifiedSessionId);

          // 🔧 立即更新本地高亮状态，使用原始索引确保UI响应
          console.log("[Vue] 🔧 设置activeChat索引:", index, "会话名:", session.showName);
          this.$store.commit("activeChat", index);

          if (window.vscodeServices) {
            const unifiedSessionService = window.vscodeServices.get('IUnifiedSessionService');
            if (unifiedSessionService && unifiedSessionService.setActiveSession) {

              // 修复：不再每次都完整同步，只在初始化时同步一次
              // 如果会话不存在，才进行单个会话的增量同步
              const allSessions = unifiedSessionService.getAllSessions?.() || [];
              const sessionExists = allSessions.some(s => s.id === unifiedSessionId);

              if (!sessionExists) {
                console.log("[Vue] 🔄 会话不存在，进行增量同步...");
                this.syncSingleSessionToUnified(session, unifiedSessionId);
                await new Promise(resolve => setTimeout(resolve, 50)); // 减少延迟
              } else {
                console.log("[Vue] ✅ 会话已存在，直接激活");
              }

              unifiedSessionService.setActiveSession(unifiedSessionId);
              console.log("[Vue] ✅ 已调用统一会话激活:", unifiedSessionId);

              // 🔧 核心修复：确保IM聊天编辑器面板打开
              if (window.vscodeServices) {
                try {
                  // 使用统一服务访问机制获取面板管理服务
                  const panelManagerService = window.vscodeServices.get('panelManagerService');
                  if (panelManagerService && panelManagerService.openChatEditor) {
                    const chatUser = {
                      id: session.targetId || session.id,
                      nickName: session.showName || session.name,
                      name: session.showName || session.name,
                      headImage: session.headImage || session.avatar,
                      headImageThumb: session.headImageThumb || session.avatar,
                      avatar: session.headImage || session.avatar,
                    };

                    panelManagerService.openChatEditor(chatUser);
                    console.log("[Vue] ✅ IM聊天编辑器面板已打开:", session.showName);
                  } else {
                    console.warn("[Vue] ⚠️ PanelManagerService或openChatEditor方法不可用");
                  }
                } catch (error) {
                  console.error("[Vue] ❌ 打开IM聊天编辑器面板失败:", error);
                }
              }


            } else {
              console.warn("[Vue] ⚠️ UnifiedSessionService不可用");
            }
          } else {
            console.warn("[Vue] ⚠️ VSCode环境不可用");
          }

        } catch (error) {
          console.error("[Vue] ❌ IM会话激活失败:", error);
        } finally {
          // 🔧 延迟释放激活锁，防止快速重复点击
          setTimeout(() => {
            this.isActivatingSession = false;
          }, this.activationCooldown);
        }
      }, 10); // 50ms防抖延迟
    },

    onDelItem(index) {
      this.$store.commit("removeChat", index);
    },

    onTop(chatIdx) {
      this.$store.commit("moveTop", chatIdx);
    },

    // 终端间通信相关方法

    // 🔧 修正：处理融合人类会话的激活（目标终端模式）
    async onActiveHumanItem(chat, index) {
      console.log("🔧 [InboxSession] 激活人类会话 - 过滤后索引:", index, "会话:", chat);

      // 🔧 关键修复：将过滤后的索引转换为原始chats数组的索引
      const realChatIndex = this.chatStore.chats.findIndex(c => {
        // 使用多个字段进行匹配，确保找到正确的会话
        const targetIdMatch = (c.targetId || c.id) === (chat.targetId || chat.id);
        const nameMatch = c.showName === chat.showName;
        const typeMatch = c.type === chat.type;

        // 🔧 修正：对于终端会话，需要额外检查终端字段
        if (chat.isTerminalChat) {
          const terminalMatch = c.isTerminalChat === chat.isTerminalChat &&
            c.senderTerminal === chat.senderTerminal;
          return targetIdMatch && nameMatch && terminalMatch;
        }

        // 🔧 兼容性：支持旧的终端收件箱会话
        if (chat.isTerminalInbox) {
          const terminalMatch = c.isTerminalInbox === chat.isTerminalInbox &&
            c.receivingTerminal === chat.receivingTerminal;
          return targetIdMatch && nameMatch && terminalMatch;
        }

        return targetIdMatch && nameMatch && typeMatch;
      });

      if (realChatIndex === -1) {
        console.error("🔧 [InboxSession] 未找到匹配的会话:", chat);
        return;
      }

      console.log("🔧 [InboxSession] 找到原始索引:", realChatIndex, "会话:", chat.showName);

      // 🔧 修正：根据会话类型进行不同的处理
      if (chat.isTerminalChat || chat.isTerminalInbox) {
        // 终端会话激活
        // await this.onActiveTerminalChatItem(chat, realChatIndex);
        // 终端会话、普通会话使用相同的处理,如果后期有问题，将以上方法的终端会话信息补全即可
        await this.onActiveItem(realChatIndex);
      } else {
        // 普通私聊/群聊激活
        await this.onActiveItem(realChatIndex);
      }
    },

    // 处理融合人类会话的删除
    onDelHumanItem(chat, index) {
      if (!chat) {
        this.$message.error('删除失败：会话对象无效');
        return;
      }

      // 通过会话对象查找真实索引
      const realIndex = this.chatStore.chats.findIndex(c =>
        c.type === chat.type && c.targetId === chat.targetId
      );

      if (realIndex === -1) {
        this.$message.error('删除失败：未找到会话');
        return;
      }

      if (chat.isTerminalInbox) {
        this.onDelTerminalInboxItem(chat, realIndex);
      } else {
        this.$store.commit("removeChat", realIndex);
      }
    },

    // 处理融合人类会话的置顶
    onTopHumanItem(chat, index) {
      if (chat.isTerminalInbox) {
        this.onTopTerminalInboxItem(chat, index);
      } else {
        this.onTop(index);
      }
    },

    // 激活终端收件箱会话
    async onActiveTerminalInboxItem(chat, index) {
      console.log('[InboxSession] 🔧 激活终端收件箱会话 - 原始索引:', index, '会话:', chat.showName, '终端:', chat.receivingTerminal);

      // 🔧 验证索引的正确性
      const actualChat = this.chatStore.chats[index];
      if (actualChat?.targetId !== chat.targetId) {
        console.warn('[InboxSession] ⚠️ 索引验证失败！传入索引对应的会话不匹配');
        console.log('[InboxSession] 期望会话:', chat.showName, chat.targetId);
        console.log('[InboxSession] 实际会话:', actualChat?.showName, actualChat?.targetId);

        // 重新查找正确的索引
        const correctIndex = this.chatStore.chats.findIndex(c =>
          (c.targetId || c.id) === (chat.targetId || chat.id) &&
          c.isTerminalInbox === chat.isTerminalInbox &&
          c.receivingTerminal === chat.receivingTerminal
        );

        if (correctIndex !== -1) {
          console.log('[InboxSession] 🔧 使用修正后的索引:', correctIndex);
          index = correctIndex;
        } else {
          console.error('[InboxSession] ❌ 无法找到匹配的会话索引');
          return;
        }
      }

      try {
        // 🔧 使用store的智能切换方法，自动处理消息加载
        await this.$store.dispatch("switchToChat", index);

        // 如果在VSCode环境，先同步会话再激活
        if (window.vscodeServices) {
          const unifiedSessionService = window.vscodeServices.get('IUnifiedSessionService');
          if (unifiedSessionService) {

            // 🔧 先同步终端收件箱会话到统一服务
            console.log('[InboxSession] 🔧 先同步终端收件箱会话到统一服务');
            const sessionId = `vue-im-${chat.targetId}`;
            this.syncSingleSessionToUnified(chat, sessionId);

            // 然后激活会话
            if (unifiedSessionService.setActiveSession) {
              console.log('[InboxSession] 🔧 终端收件箱会话ID:', sessionId);
              unifiedSessionService.setActiveSession(sessionId);
            }
          }
        }

        console.log('[InboxSession] ✅ 终端收件箱会话激活完成');
      } catch (error) {
        console.error('[InboxSession] 激活终端收件箱会话失败:', error);
        this.$message.error('加载终端会话失败');
      }
    },

    // 删除终端收件箱会话
    onDelTerminalInboxItem(chat, index) {
      this.$confirm(`确定要删除${chat.showName}的会话记录吗？`, '确认删除', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.$store.commit("removeChat", index);
        this.$message.success('终端会话已删除');
      }).catch(() => {
        // 用户取消
      });
    },

    // 置顶终端收件箱会话
    onTopTerminalInboxItem(chat, index) {
      this.$store.commit("moveTop", index);
      this.$message.success(`${chat.showName}已置顶`);
    },

    // 加载终端收件箱消息
    async loadTerminalInboxMessages(chat) {
      try {
        console.log(`[InboxSession] 开始加载${chat.showName}历史消息...`);

        const messages = await this.$store.dispatch('loadTerminalInboxHistory', {
          receivingTerminal: chat.receivingTerminal,
          page: 1,
          size: 50
        });

        console.log(`[InboxSession] 从服务器获取到${messages.length}条消息`);

        // 🔧 修复：通过store mutation更新消息，确保响应式更新
        if (messages && messages.length > 0) {
          // 处理消息格式，确保字段完整
          const processedMessages = messages.map(msg => {
            // 确保时间字段是数字格式
            const sendTime = typeof msg.sendTime === 'string' ?
              new Date(msg.sendTime).getTime() :
              (msg.sendTime || Date.now());

            return {
              ...msg,
              sendTime: sendTime,
              // 确保必要字段存在
              id: msg.id || msg.tmpId || `terminal_${Date.now()}_${Math.random()}`,
              selfSend: msg.selfSend !== undefined ? msg.selfSend : false,
              sendNickName: msg.sendNickName || this.generateTerminalSenderName(msg),
              type: msg.type || 0, // 默认文本消息
              status: msg.status || 1 // 已发送状态
            };
          });

          // 🔧 使用批量插入方法，避免频繁保存
          this.$store.commit("batchInsertMessages", {
            messages: processedMessages,
            chatInfo: chat
          });

          // 更新会话最后消息信息
          const latestMessage = processedMessages[processedMessages.length - 1];
          chat.lastContent = latestMessage.content;
          chat.lastSendTime = latestMessage.sendTime;

          console.log(`[InboxSession] ✅ 成功加载并插入${processedMessages.length}条终端消息`);
        } else {
          console.log(`[InboxSession] 📭 ${chat.showName}无历史消息`);
        }

      } catch (error) {
        console.error('[InboxSession] 加载终端收件箱消息失败:', error);
        this.$message.error('加载终端消息失败');
      }
    },

    // 生成终端发送者名称
    generateTerminalSenderName(msg) {
      const userInfo = this.$store.state.userStore.userInfo;
      const userName = userInfo?.nickName || '用户';

      if (msg.senderTerminal !== undefined) {
        const terminalName = this.getTerminalName(msg.senderTerminal);
        return `${userName}(${terminalName}端)`;
      }

      return userName;
    },

    // 生成会话显示Key（用于Vue的key属性）
    generateChatKey(chat) {
      if (chat.isTerminalInbox) {
        return `terminal-${chat.receivingTerminal}`;
      } else {
        return `${chat.type}-${chat.targetId}`;
      }
    },

    // 🔧 修正：增强聊天显示信息（支持目标终端模式）
    enrichChatDisplay(chat) {
      const enriched = { ...chat };

      // 🔧 修正：处理终端会话显示
      if (chat.isTerminalChat || chat.isTerminalInbox) {
        // 确保终端会话有正确的显示信息
        if (!enriched.showName) {
          let terminalType;
          if (chat.isTerminalChat) {
            terminalType = chat.senderTerminal;
          } else if (chat.isTerminalInbox) {
            terminalType = chat.receivingTerminal;
          }

          enriched.showName = this.getTerminalName(terminalType) + '端';
        }

        if (!enriched.headImage) {
          let terminalType;
          if (chat.isTerminalChat) {
            terminalType = chat.senderTerminal;
          } else if (chat.isTerminalInbox) {
            terminalType = chat.receivingTerminal;
          }

          enriched.headImage = this.getTerminalIcon(terminalType);
        }

        // 🔧 修正：设置终端会话的特殊样式标识
        enriched.chatType = chat.isTerminalChat ? 'terminal-chat' : 'terminal-inbox';

        console.log(`🔧 [InboxSession] 丰富终端会话显示: ${enriched.showName}`);
      }

      return enriched;
    },

    // 🔧 新增：获取终端名称
    getTerminalName(terminalType) {
      const terminalNames = {
        0: 'Web',
        1: 'App',
        2: 'VSCode',
        3: 'PC',
        4: '浏览器',
        5: 'MCP',
        6: '小龙虾'
      };
      return terminalNames[terminalType] || '未知';
    },

    // 🔧 新增：获取终端图标
    getTerminalIcon(terminalType) {
      const terminalIcons = {
        0: '🌐',  // Web
        1: '📱',  // App
        2: '💻',  // VSCode
        3: '🖥️',  // PC
        4: '🔌',  // 浏览器
        5: '🤖',  // MCP
        6: '🦞'   // 小龙虾
      };
      return terminalIcons[terminalType] || '❓';
    },

    // 初始化统一会话服务
    initializeUnifiedSessionService() {
      console.log("[InboxSession] 初始化统一会话服务");

      // 获取vscodeServices实例
      if (window.vscodeServices) {
        // ✅ 避免将VSCode服务对象赋值给Vue响应式数据
        // this.unifiedSessionService = window.vscodeServices;
        console.log("[InboxSession] 统一会话服务初始化完成");
      } else {
        console.error("[InboxSession] vscodeServices不可用");
      }
    },



    // 从统一会话管理器加载AI对话
    async loadAISessionsFromUnified() {
      try {
        this.loadingAI = true;


        // 尝试从多个可能的服务获取AI对话
        let aiSessions = [];
        let rooCodeTasks = [];

        // 获取RooCode任务历史
        try {
          console.log("[InboxSession] 🔧 开始获取RooCode任务历史...");
          
          if (window.vscodeServices && window.vscodeServices.get) {
            console.log("[InboxSession] 🔧 尝试获取IRooCodeTaskHistoryService...");
            const rooCodeService = window.vscodeServices.get('IRooCodeTaskHistoryService');
            console.log("[InboxSession] 🔧 IRooCodeTaskHistoryService:", !!rooCodeService);
            
            if (rooCodeService) {
              console.log("[InboxSession] 🔧 调用getTaskHistory方法...");
              const taskData = await rooCodeService.getTaskHistory();
              console.log("[InboxSession] 🔧 获取到RooCode任务:", taskData);
              
              if (taskData && taskData.tasks && taskData.tasks.length > 0) {
                console.log("[InboxSession] 🔧 开始转换RooCode任务为AI会话格式...");
                // 将RooCode任务转换为AI会话格式
                rooCodeTasks = taskData.tasks.map(task => {
                  console.log("[InboxSession] 🔧 转换任务:", task);
                  const taskSession = rooCodeService.convertToAISession(task);
                  return {
                    ...taskSession,
                    isRooCodeTask: true,
                    originalTask: task
                  };
                });
                console.log("[InboxSession] 🔧 转换后的RooCode任务数:", rooCodeTasks.length);
              } else {
                console.log("[InboxSession] 🔧 没有获取到RooCode任务或任务数组为空");
              }
            } else {
              console.warn("[InboxSession] 🔧 IRooCodeTaskHistoryService服务不可用");
            }
          } else {
            console.warn("[InboxSession] 🔧 window.vscodeServices不可用");
          }
        } catch (error) {
          console.error("[InboxSession] 获取RooCode任务历史失败:", error);
        }
        // 合并RooCode任务到AI会话列表
        if (rooCodeTasks.length > 0) {
          console.log("[InboxSession] 🔧 合并RooCode任务到AI会话列表...");
          aiSessions = [...rooCodeTasks, ...aiSessions];
        }

        console.log("[InboxSession] 🔧 === AI对话获取总结 ===");
        console.log("[InboxSession] 🔧 最终获取到AI对话:", aiSessions.length, "个");
        console.log("[InboxSession] 🔧 其中RooCode任务:", rooCodeTasks.length, "个");
        console.log("[InboxSession] 🔧 原始AI对话数据:", aiSessions);




        console.log("[InboxSession] AI对话数据处理完成:", this.aiSessions.length, "个");

      } catch (error) {
        console.error("[InboxSession] 从统一会话管理器加载AI对话失败:", error);
        this.aiSessions = [];
        throw error;
      } finally {
        this.loadingAI = false;
      }
    },

    // 生成AI会话摘要（简洁版，只显示消息条数） - 带缓存优化
    generateAISessionSummary(session) {
      // 使用缓存key避免重复计算
      const cacheKey = `${session.id}_${session.metadata?.messageCount || session.messageCount || 0}`;
      if (!this._aiSummaryCache) {
        this._aiSummaryCache = new Map();
      }

      // 检查缓存
      if (this._aiSummaryCache.has(cacheKey)) {
        return this._aiSummaryCache.get(cacheKey);
      }

      // 适应统一会话管理器的数据结构
      const messageCount = session.metadata?.messageCount || session.messageCount || 0;

      // 减少日志输出，避免重复调用时产生大量日志
      if (this.debugMode) {
        console.log("[InboxSession] generateAISessionSummary调用，会话ID:", session.id, "消息数量:", messageCount);
        console.log("[InboxSession] 会话数据结构:", {
          metadataMessageCount: session.metadata?.messageCount,
          sessionMessageCount: session.messageCount,
          finalMessageCount: messageCount
        });
      }

      let summary;
      if (messageCount === 0) {
        if (this.debugMode) console.log("[InboxSession] AI对话无消息，返回默认值");
        summary = "新建的AI对话";
      } else if (messageCount === 1) {
        summary = "1 条对话";
      } else {
        summary = `${messageCount} 条对话`;
      }

      // 缓存结果
      this._aiSummaryCache.set(cacheKey, summary);

      // 限制缓存大小，避免内存泄漏
      if (this._aiSummaryCache.size > 100) {
        const firstKey = this._aiSummaryCache.keys().next().value;
        this._aiSummaryCache.delete(firstKey);
      }

      return summary;
    },

    // 获取AI头像
    getAIAvatar(model) {
      const avatars = {
        "Claude-3.5-Sonnet": "🤖",
        "GPT-4": "🧠",
        Gemini: "💎",
        default: "🤖",
      };
      return avatars[model] || avatars.default;
    },

    // 🌟 AI对话激活 - 只调用统一服务（带防抖和循环保护）
    onActiveAIItem(sessionIndex) {
      const aiSession = this.aiSessions[sessionIndex];
      if (!aiSession) {
        console.error("[InboxSession] AI对话不存在，索引:", sessionIndex);
        return;
      }

      // 检查是否是RooCode任务
      if (aiSession.isRooCodeTask) {
        console.log("[InboxSession] 激活RooCode任务:", aiSession.showName);
        this.onActiveRooCodeTask(aiSession);
        return;
      }

      // 🔧 生成统一会话ID
      const unifiedSessionId = `react-${aiSession.aiThreadId || aiSession.targetId || aiSession.id}`;

      // 🔧 防循环检查：如果正在激活相同会话，直接返回
      if (this.isActivatingSession && this.lastActivatedSessionId === unifiedSessionId) {
        console.log("[Vue] 🛡️ 检测到重复激活AI会话，忽略:", unifiedSessionId);
        return;
      }

      // 🔧 清除之前的防抖计时器
      if (this.activationDebounceTimer) {
        clearTimeout(this.activationDebounceTimer);
      }

      // 🔧 设置防抖计时器
      this.activationDebounceTimer = setTimeout(async () => {
        try {
          // 🔧 设置激活状态锁
          this.isActivatingSession = true;
          this.lastActivatedSessionId = unifiedSessionId;

          console.log("[Vue] 🎯 用户选择AI对话:", aiSession.showName, "ID:", unifiedSessionId);

          if (window.vscodeServices) {
            const unifiedSessionService = window.vscodeServices.get('IUnifiedSessionService');
            if (unifiedSessionService && unifiedSessionService.setActiveSession) {
              unifiedSessionService.setActiveSession(unifiedSessionId);
              console.log("[Vue] ✅ 已调用统一AI会话激活:", unifiedSessionId);

              // 🔧 核心修复：确保AI聊天编辑器面板打开
              if (window.vscodeServices.openAIChatEditor) {
                try {
                  window.vscodeServices.openAIChatEditor(aiSession);
                  console.log("[Vue] ✅ AI聊天编辑器面板已打开:", aiSession.showName);
                } catch (error) {
                  console.error("[Vue] ❌ 打开AI聊天编辑器面板失败:", error);
                }
              }


            } else {
              console.warn("[Vue] ⚠️ UnifiedSessionService不可用");
            }
          } else {
            console.warn("[Vue] ⚠️ VSCode环境不可用");
          }

        } catch (error) {
          console.error("[Vue] ❌ AI会话激活失败:", error);
        } finally {
          // 🔧 延迟释放激活锁，防止快速重复点击
          setTimeout(() => {
            this.isActivatingSession = false;
          }, this.activationCooldown);
        }
      }, 50); // 50ms防抖延迟
    },

    // 激活RooCode任务
    async onActiveRooCodeTask(aiSession) {
      try {
        console.log("[InboxSession] 激活RooCode任务:", aiSession);
        console.log("[InboxSession] 原始任务对象:", aiSession.originalTask);
        
        if (window.vscodeServices) {
          const rooCodeService = window.vscodeServices.get('IRooCodeTaskHistoryService');
          if (rooCodeService && rooCodeService.activateTask) {
            // 使用原始任务的ID，不要使用加了前缀的ID
            const taskId = aiSession.originalTask.id;
            console.log("[InboxSession] 准备激活任务ID:", taskId);
            const success = await rooCodeService.activateTask(taskId);
            
            if (success) {
              console.log("[InboxSession] RooCode任务激活成功:", taskId);
              this.activeAISession = aiSession;
              
              // 打开RooCode面板
              if (window.vscodeServices.get('ICommandService')) {
                await window.vscodeServices.get('ICommandService').executeCommand('roo-cline.SidebarProvider.focus');
              }
            } else {
              console.error("[InboxSession] RooCode任务激活失败:", taskId);
              this.$message?.error("激活任务失败");
            }
          }
        }
      } catch (error) {
        console.error("[InboxSession] 激活RooCode任务失败:", error);
        this.$message?.error("激活任务失败");
      }
    },

    // 删除AI对话
    async onDelAIItem(sessionIndex) {
      const aiSession = this.aiSessions[sessionIndex];
      if (!aiSession) return;
      
      // 检查是否是RooCode任务
      if (aiSession.isRooCodeTask) {
        console.log("[InboxSession] 删除RooCode任务:", aiSession.showName);
        this.onDelRooCodeTask(aiSession);
        return;
      }

      try {
        console.log("[Vue] 删除AI对话:", aiSession.showName);

        // 🔔 通过统一会话管理器删除AI对话
        const threadId = aiSession.aiThreadId || aiSession.targetId || aiSession.id;

        if (window.SharedServicesAccessor) {
          // 1. 获取统一会话服务
          const unifiedSessionService = window.SharedServicesAccessor.get('IUnifiedSessionService');

          // 2. 获取ChatThreadService来执行实际删除
          const chatThreadService = window.SharedServicesAccessor.get('IChatThreadService');

          if (chatThreadService && chatThreadService.deleteThread) {
            console.log("[Vue] 通过ChatThreadService删除AI线程:", threadId);

            // 调用ChatThreadService的deleteThread方法，它会自动触发统一会话管理器的删除事件
            chatThreadService.deleteThread(threadId);

            // 删除FTS索引
            if (unifiedSessionService && unifiedSessionService.deleteSessionIndex) {
              try {
                await unifiedSessionService.deleteSessionIndex(threadId, 'react-ai');
                console.log("[Vue] AI对话FTS索引已删除");
              } catch (error) {
                console.warn("[Vue] 删除AI对话FTS索引失败:", error);
              }
            }

            console.log("[Vue] AI对话删除完成:", threadId);
            this.$message?.success("AI对话已删除");

          } else {
            console.warn("[Vue] ChatThreadService不可用");
            this.$message?.error("删除服务不可用");
            return;
          }
        } else {
          console.warn("[Vue] VSCode环境不可用");
          this.$message?.error("VSCode环境不可用");
          return;
        }

        // 3. 从本地列表移除（通常会被统一会话服务的事件自动更新，但为了立即响应也手动移除）
        const realIndex = this.aiSessions.findIndex(
          (s) => s.id === aiSession.id
        );
        if (realIndex >= 0) {
          this.aiSessions.splice(realIndex, 1);
        }

        // 4. 如果删除的是当前活跃会话，清除选择
        if (this.activeAISession?.id === aiSession.id) {
          this.activeAISession = null;
          this.$bus.$emit("ai:chat:cleared");
        }

      } catch (error) {
        console.error("[Vue] 删除AI对话失败:", error);
        this.$message?.error("删除失败");
      }
    },

    // 删除RooCode任务
    async onDelRooCodeTask(aiSession) {
      try {
        if (!confirm("确定要删除这个RooCode任务吗？")) {
          return;
        }
        
        console.log("[InboxSession] 删除RooCode任务:", aiSession.originalTask);
        
        if (window.vscodeServices) {
          const rooCodeService = window.vscodeServices.get('IRooCodeTaskHistoryService');
          if (rooCodeService && rooCodeService.deleteTask) {
            const taskId = aiSession.originalTask.id;
            const success = await rooCodeService.deleteTask(taskId);
            
            if (success) {
              console.log("[InboxSession] RooCode任务删除成功:", taskId);
              
              // 从列表中移除
              const index = this.aiSessions.findIndex(s => s.id === aiSession.id);
              if (index !== -1) {
                this.aiSessions.splice(index, 1);
              }
              
              this.$message?.success("任务已删除");
            } else {
              console.error("[InboxSession] RooCode任务删除失败:", taskId);
              this.$message?.error("删除任务失败");
            }
          }
        }
      } catch (error) {
        console.error("[InboxSession] 删除RooCode任务失败:", error);
        this.$message?.error("删除任务失败");
      }
    },

    // 置顶AI对话
    onTopAIItem(sessionIndex) {
      const aiSession = this.aiSessions[sessionIndex];
      if (!aiSession) return;

      console.log("🔝 置顶AI对话:", aiSession.showName);

      // 找到原始数组中的会话并更新时间戳
      const realIndex = this.aiSessions.findIndex((s) => s.id === aiSession.id);
      if (realIndex >= 0) {
        const session = this.aiSessions[realIndex];
        const currentTime = Date.now();

        // 🔧 更新所有时间字段，确保排序时该会话排在最前面
        session.lastSendTime = currentTime;
        session.lastMessageTime = currentTime;
        session.lastModified = new Date(currentTime).toISOString();

        console.log("🔝 AI会话置顶完成，新时间戳:", currentTime);

        // 不需要手动移动，因为 filteredAiSessions 计算属性会自动重新排序
        // Vue 的响应式系统会检测到时间字段的变化并重新计算排序
      } else {
        console.warn("🔝 未找到要置顶的AI会话:", aiSession.showName);
      }
    },

    // 截断文本
    truncateText(text, maxLength) {
      if (!text || text.length <= maxLength) return text;
      return text.substring(0, maxLength) + "...";
    },

    // 设置事件监听
    setupEventListeners() {
      console.log("[InboxSession] 设置事件监听器");

      try {
        // 订阅统一会话管理器的事件
        const unifiedSessionService = this.$vscode.get('IUnifiedSessionService');
        if (unifiedSessionService && unifiedSessionService.onSessionChanged) {
          // VSCode Event返回的是IDisposable对象，有dispose方法
          const disposable = unifiedSessionService.onSessionChanged(
            this.handleSessionEvent.bind(this)
          );

          if (disposable && typeof disposable.dispose === 'function') {
            this.sessionEventUnsubscribe = () => disposable.dispose();
            console.log("[InboxSession] 已订阅统一会话事件");
          } else {
            console.warn("[InboxSession] 事件订阅返回的不是有效的disposable对象");
            this.sessionEventUnsubscribe = null;
          }
        } else {
          console.warn("[InboxSession] 统一会话服务或onSessionChanged事件不可用");
          this.sessionEventUnsubscribe = null;
        }

        // 添加DOM事件监听器（用于跨端事件通信的备用方案）
        window.addEventListener('unified-session-created', this.handleSessionCreated);

        console.log("[InboxSession] 事件监听器设置完成");
      } catch (error) {
        console.error("[InboxSession] 设置事件监听器失败:", error);
        this.sessionEventUnsubscribe = null;
      }
    },

    // 🌟 事件响应 - 只处理UI和业务逻辑，绝不回调（带循环保护）
    handleSessionEvent(event) {
      console.log("[Vue] 收到会话事件:", event?.type, event?.sessionId);

      if (!event || !event.type) {
        return;
      }

      // 🔧 防循环保护：如果当前正在激活会话，且事件来自刚激活的会话，则跳过处理
      if (event.type === 'unified-session-activated' &&
        this.isActivatingSession &&
        event.sessionId === this.lastActivatedSessionId) {
        console.log("[Vue] 🛡️ 检测到自己激活产生的事件，跳过处理:", event.sessionId);
        return;
      }

      // 根据事件类型处理不同的逻辑
      switch (event.type) {
        case 'unified-session-activated':
          if (event.sessionType === 'vue-im') {
            this.handleImSessionActivated(event);
          } else if (event.sessionType === 'react-ai') {
            this.handleAiSessionActivated(event);
          }
          break;
        case 'unified-session-deleted':
          this.handleSessionDeleted(event);
          break;
        case 'unified-session-added':
        case 'unified-session-created':
          console.log('[Vue] 会话已创建/添加，处理新建会话');
          this.handleSessionCreated(event);
          break;
        case 'unified-session-updated':
          console.log('[Vue] 会话已更新');
          break;
        default:
          console.log('[Vue] 未处理的会话事件类型:', event.type);
      }

      // 🚫 绝对不能调用 setActiveSession！
    },

    // 处理会话删除事件
    handleSessionDeleted(event) {
      if (!event.sessionId || !event.sessionType) {
        console.warn('[Vue] 会话删除事件缺少必要信息');
        return;
      }

      console.log(`[Vue] 处理会话删除: ${event.sessionType}:${event.sessionId}`);

      if (event.sessionType === 'react-ai') {
        // 处理AI会话删除
        const sessionIdToRemove = event.sessionId.replace('react-', ''); // 移除前缀

        // 从AI会话列表中移除
        const originalLength = this.aiSessions.length;
        this.aiSessions = this.aiSessions.filter(session => {
          const sessionThreadId = session.aiThreadId || session.targetId || session.id;
          return sessionThreadId !== sessionIdToRemove;
        });

        const removedCount = originalLength - this.aiSessions.length;
        if (removedCount > 0) {
          console.log(`[Vue] 已从AI会话列表移除 ${removedCount} 个会话`);

          // 如果删除的是当前活跃会话，清除选择
          if (this.activeAISession &&
            (this.activeAISession.aiThreadId === sessionIdToRemove ||
              this.activeAISession.targetId === sessionIdToRemove ||
              this.activeAISession.id === sessionIdToRemove)) {
            console.log('[Vue] 清除当前活跃AI会话');
            this.activeAISession = null;
            this.$bus.$emit("ai:chat:cleared");
          }
        } else {
          console.log('[Vue] 未找到要删除的AI会话:', sessionIdToRemove);
        }

      } else if (event.sessionType === 'vue-im') {
        // 处理IM会话删除（如果需要的话）
        console.log('[Vue] 收到Vue IM会话删除事件:', event.sessionId);
        // 这里可以添加处理逻辑，如果Vue侧也需要响应IM会话的删除
      }
    },

    // 处理会话创建事件
    handleSessionCreated(event) {
      console.log("[InboxSession] 收到会话创建事件:", event.detail || event);

      try {
        const eventData = event.detail || event;
        const { sessionType, sessionId, sessionData } = eventData;

        // 只处理React AI会话创建
        if (sessionType === 'react-ai') {
          console.log("[InboxSession] React侧创建了新AI对话:", sessionId);

          // 重新加载AI会话列表
          this.loadAllSessions();

          // 自动激活新创建的会话
          setTimeout(() => {
            const newSession = this.aiSessions.find(session =>
              session.id === sessionId || session.aiThreadId === sessionId
            );
            if (newSession) {
              console.log("[InboxSession] 自动激活新创建的AI会话:", newSession.showName);
              this.activeAISession = newSession;
            }
          }, 500); // 等待加载完成
        }
      } catch (error) {
        console.error("[InboxSession] 处理会话创建事件失败:", error);
      }
    },

    // 处理IM会话激活
    async handleImSessionActivated(event) {
      console.log("[Vue] 🔧 处理IM会话激活:", event.session?.title);
      console.log("[Vue] 🔧 事件数据:", event);

      // 找到对应的本地会话并激活
      const sessionData = event.session;
      if (!sessionData) {
        console.warn("[Vue] 🔧 事件中没有会话数据");
        return;
      }

      // 🔧 修复：更灵活的targetId获取
      const targetId = sessionData.targetId || sessionData.sessionId || sessionData.realSessionRef;
      if (!targetId) {
        console.warn("[Vue] 🔧 事件中没有有效的targetId:", sessionData);
        return;
      }

      console.log("[Vue] 🔧 查找targetId:", targetId, "类型:", typeof targetId);
      console.log("[Vue] 🔧 当前聊天列表:", this.chatStore.chats?.map(c => ({
        targetId: c.targetId,
        id: c.id,
        showName: c.showName
      })));

      // 🔧 关键修复：如果chatStore.chats为空或找不到会话，先重新加载数据
      if (!this.chatStore.chats || this.chatStore.chats.length === 0) {
        console.log("[Vue] 🔄 聊天数据为空，重新加载...");
        await this.loadHumanChats();
        console.log("[Vue] 🔄 重新加载后的聊天列表:", this.chatStore.chats?.map(c => ({
          targetId: c.targetId,
          id: c.id,
          showName: c.showName
        })));
      }

      const targetIdStr = targetId.toString();
      let matchingSession = this.chatStore.chats?.find(
        chat => {
          const chatTargetId = (chat.targetId || chat.id)?.toString();
          const match = chatTargetId === targetIdStr;
          if (match) {
            console.log("[Vue] 🔧 找到匹配会话:", chat.showName);
          }
          return match;
        }
      );

      // 🔧 如果第一次没找到，再尝试一次重新加载并查找
      if (!matchingSession && this.chatStore.chats && this.chatStore.chats.length > 0) {
        console.log("[Vue] 🔄 首次匹配失败，重新加载数据再次尝试...");
        await this.loadHumanChats();
        matchingSession = this.chatStore.chats?.find(
          chat => {
            const chatTargetId = (chat.targetId || chat.id)?.toString();
            const match = chatTargetId === targetIdStr;
            if (match) {
              console.log("[Vue] 🔧 重新加载后找到匹配会话:", chat.showName);
            }
            return match;
          }
        );
      }

      if (matchingSession) {
        const actualIndex = this.chatStore.chats.indexOf(matchingSession);
        console.log("[Vue] 🔧 激活会话索引:", actualIndex, "会话名:", matchingSession.showName);

        // 只做UI和业务逻辑，不回调setActiveSession
        this.$store.commit("activeChat", actualIndex);
        this.activeAISession = null; // 清除AI会话选择

        console.log("[Vue] 🔧 会话已在本地激活，EditorChat会通过统一事件接收");

        // VSCode环境中打开聊天编辑器
        if (window.SharedServicesAccessor?.openChatEditor) {
          const chatUser = {
            id: matchingSession.targetId || matchingSession.id,
            nickName: matchingSession.showName || matchingSession.name,
            name: matchingSession.showName || matchingSession.name,
            headImage: matchingSession.headImage || matchingSession.avatar,
            headImageThumb: matchingSession.headImageThumb || matchingSession.avatar,
            avatar: matchingSession.headImage || matchingSession.avatar,
          };

          try {
            window.SharedServicesAccessor.openChatEditor(chatUser);
            console.log("[Vue] 🔧 Chat editor opened successfully");

            // 🌟 确保聊天记录面板打开 - 通过事件总线通知EditorChat
            if (this.$bus) {
              this.$bus.$emit('chat:ensure-panel-visible', {
                sessionType: 'IM',
                sessionData: matchingSession,
                reason: 'session-activated'
              });
              console.log("[Vue] 🔧 已发送面板可见确保事件");
            }
          } catch (error) {
            console.error("[Vue] ❌ Failed to open chat editor:", error);
          }
        }
      } else {
        console.warn("[Vue] ❌ 未找到匹配的会话，targetId:", targetId);
        console.log("[Vue] 🔧 可用会话ID列表:", this.chatStore.chats?.map(c => c.targetId || c.id));

        // 🔧 如果找不到匹配会话，强制清除选择
        console.log("[Vue] 🔧 强制清除会话选择");
        this.$store.commit("clearActiveChat");
        this.activeAISession = null;
      }
    },

    // 处理AI会话激活
    handleAiSessionActivated(event) {
      console.log("[Vue] 处理AI会话激活:", event.session?.title);

      const sessionData = event.session;
      if (!sessionData) return;

      // 找到对应的AI会话并激活
      const matchingAISession = this.aiSessions?.find(
        ai => ai.id === sessionData.sessionId || ai.aiThreadId === sessionData.sessionId
      );

      if (matchingAISession) {
        // 只做UI和业务逻辑，不回调setActiveSession
        this.activeAISession = matchingAISession;
        this.$store.commit("clearActiveChat"); // 清除人类聊天选择

        // 通知EditorChat切换到AI模式
        this.$bus.$emit("ai:chat:selected", matchingAISession);

        // VSCode环境中打开AI聊天编辑器
        if (window.SharedServicesAccessor?.openAIChatEditor) {
          try {
            window.SharedServicesAccessor.openAIChatEditor(matchingAISession);
            console.log("[Vue] AI chat editor opened successfully");

            // 🌟 确保聊天记录面板打开 - 通过事件总线通知EditorChat
            if (this.$bus) {
              this.$bus.$emit('chat:ensure-panel-visible', {
                sessionType: 'AI',
                sessionData: matchingAISession,
                reason: 'session-activated'
              });
              console.log("[Vue] 🔧 已发送AI面板可见确保事件");
            }
          } catch (error) {
            console.error("[Vue] Failed to open AI chat editor:", error);
          }
        }
      }
    },

    // 打开AI聊天
    openAIChat(aiSession) {
      console.log("打开AI会话:", aiSession.showName);

      // 设置为活跃AI会话
      this.activeAISession = aiSession;

      // 清除人类聊天选择
      this.$store.commit("clearActiveChat");

      // 通过事件总线通知切换到AI简化界面
      if (this.$bus) {
        this.$bus.$emit("ai:chat:selected", aiSession);
        console.log("[InboxSession] 已通过事件总线发送 ai:chat:selected 事件");
      }

      // 同时发出组件事件（为了兼容性）
      this.$emit("ai-chat-selected", aiSession);

      // VSCode环境中，同时打开编辑器中的AI聊天窗口
      if (window.SharedServicesAccessor && window.SharedServicesAccessor.openAIChatEditor) {
        try {
          window.SharedServicesAccessor.openAIChatEditor(aiSession);
          console.log("[InboxSession] AI chat editor opened successfully");

          // 🌟 确保聊天记录面板打开 - 通过事件总线通知EditorChat
          if (this.$bus) {
            this.$bus.$emit('chat:ensure-panel-visible', {
              sessionType: 'AI',
              sessionData: aiSession,
              reason: 'direct-open'
            });
            console.log("[InboxSession] 🔧 已发送AI面板可见确保事件（直接打开）");
          }
        } catch (error) {
          console.error("[InboxSession] Failed to open AI chat editor:", error);
        }
      } else {
        console.log(
          "[InboxSession] VSCode环境中，同时打开编辑器中的AI聊天窗口，这个逻辑分支没有执行 - openAIChatEditor方法不存在"
        );
      }
    },

    // 获取人类会话的实际索引
    getHumanSessionIndex(filteredIndex) {
      const filteredSessions = this.filteredHumanSessions;
      if (filteredIndex >= 0 && filteredIndex < filteredSessions.length) {
        const session = filteredSessions[filteredIndex];
        return this.chatStore.chats.findIndex((chat) => chat === session);
      }
      return filteredIndex;
    },

    // 格式化时间
    formatTime(timestamp) {
      if (!timestamp) return "";

      const date = new Date(timestamp);
      const now = new Date();
      const diff = now.getTime() - date.getTime();

      if (diff < 60000) return "刚刚";
      if (diff < 3600000) return `${Math.floor(diff / 60000)}分钟前`;
      if (diff < 86400000) return `${Math.floor(diff / 3600000)}小时前`;

      return date.toLocaleDateString("zh-CN", {
        month: "short",
        day: "numeric",
      });
    },

    // 刷新所有会话
    async refreshSessions() {
      try {
        console.log("刷新会话列表");

        // 重新加载AI对话
        await this.loadAllSessions();

        this.$message?.success("对话列表已刷新");
      } catch (error) {
        console.error("刷新对话失败:", error);
        this.$message?.error("刷新失败");
      }
    },

    // 格式化同步时间
    formatSyncTime(timestamp) {
      const now = Date.now();
      const diff = now - timestamp;

      if (diff < 60000) {
        return "刚刚";
      } else if (diff < 3600000) {
        return `${Math.floor(diff / 60000)}分钟前`;
      } else {
        return new Date(timestamp).toLocaleTimeString("zh-CN", {
          hour: "2-digit",
          minute: "2-digit",
        });
      }
    },

    // ========== VSCode聊天工作区同步功能 ==========

    /**
     * 🔧 通过UnifiedSessionService统一设置活跃会话
     */
    setActiveSessionInUnified(session) {
      try {
        console.log('🎯 [InboxSession] 通过统一服务设置活跃会话:', session.showName);

        if (window.vscodeServices) {
          // 获取UnifiedSessionService
          const unifiedSessionService = window.vscodeServices.get('IUnifiedSessionService');
          if (unifiedSessionService && unifiedSessionService.setActiveSession) {

            // 🔧 构造统一的会话ID
            let unifiedSessionId;
            if (session.type === 'ai' || session.isAI) {
              // AI会话：使用react-前缀
              unifiedSessionId = `react-${session.aiThreadId || session.targetId || session.id}`;
            } else {
              // 🔧 修复：所有IM会话（包括终端收件箱）都使用vue-im-前缀
              // 通过targetId中的格式（如1_0）来区分普通会话和终端收件箱
              unifiedSessionId = `vue-im-${session.targetId || session.id}`;
            }

            console.log('🔧 [InboxSession] 设置统一活跃会话ID:', unifiedSessionId);

            // 通过统一服务设置活跃会话
            unifiedSessionService.setActiveSession(unifiedSessionId);
            console.log('✅ [InboxSession] 统一活跃会话设置成功');

          } else {
            console.log('⚠️ [InboxSession] UnifiedSessionService不可用');
          }
        } else {
          console.log('⚠️ [InboxSession] VSCode环境不可用');
        }

      } catch (error) {
        console.error('❌ [InboxSession] 统一活跃会话设置失败:', error);
      }
    },

    // 🔧 移除列表同步方法，改为按需单向同步

    /**
     * 转换会话数据为聊天工作区标准格式
     */
    transformSessionForWorkspace(session) {
      if (!session) return null;

      // 统一的会话数据格式
      return {
        id: session.targetId || session.id || `session_${Date.now()}`,
        sessionId: session.targetId || session.id,
        showName: session.showName || session.name || '未知会话',
        type: session.type || (session.isAI ? 'ai' : 'PRIVATE'),
        headImage: session.headImage || session.avatar || '💬',
        avatar: session.headImage || session.avatar || '💬',
        lastContent: session.lastContent || session.lastMessage || '',
        lastMessage: session.lastContent || session.lastMessage || '',
        lastSendTime: session.lastSendTime || session.lastMessageTime || Date.now(),
        lastMessageTime: session.lastSendTime || session.lastMessageTime || Date.now(),
        unreadCount: session.unreadCount || 0,
        messageCount: session.messageCount || 0,

        // AI相关字段
        isAI: Boolean(session.type === 'ai' || session.isAI),
        aiModel: session.aiModel,
        aiThreadId: session.aiThreadId,
        hasComplexTools: Boolean(session.hasComplexTools),
        isThinking: Boolean(session.isThinking),

        // 原始数据备份
        originalData: session
      };
    },

    /**
     * 🔧 初始化VSCode工作区同步（单向，无回调）
     */
    initializeWorkspaceSync() {
      try {
        console.log('🔧 [InboxSession] 初始化工作区同步（单向模式）...');

        if (window.SharedServicesAccessor) {
          console.log('✅ [InboxSession] VSCode环境可用');
          // 🔧 不再暴露回调处理器，避免循环调用
          console.log('✅ [InboxSession] 单向同步模式已启用');
        } else {
          console.log('⚠️ [InboxSession] VSCode环境不可用');
        }

      } catch (error) {
        console.error('❌ [InboxSession] 工作区同步初始化失败:', error);
      }
    },

    // 🔧 移除VSCode回调处理方法，避免循环调用
    // handleVSCodeActiveSessionChanged 方法已删除，改为单向同步模式

    /**
     * 🔧 同步Vue会话数据到UnifiedSessionService
     */
    syncSessionsToUnified() {
      try {
        console.log('🔄 [InboxSession] 同步会话到统一服务');

        if (!window.SharedServicesAccessor) {
          console.log('⚠️ [InboxSession] VSCode环境不可用');
          return;
        }

        // 获取UnifiedSessionService
        const unifiedSessionService = window.SharedServicesAccessor.get('IUnifiedSessionService');
        if (!unifiedSessionService || !unifiedSessionService.syncVueSessionsFromWebView) {
          console.log('⚠️ [InboxSession] UnifiedSessionService不可用');
          return;
        }

        // 🔧 只同步人类会话，AI会话由UnifiedSessionService直接管理
        const humanSessions = [];

        // 添加人类会话（包括终端收件箱会话）
        this.filteredHumanSessions.forEach((session, index) => {
          const syncSession = {
            id: session.targetId || session.id,
            sessionId: session.targetId || session.id,
            showName: session.showName || session.name || '未知会话',
            // 🔧 修复：统一使用vue-im类型，通过ID前缀和额外字段区分
            type: 'vue-im',
            lastSendTime: session.lastSendTime || session.lastMessageTime || Date.now(),
            lastMessageTime: session.lastSendTime || session.lastMessageTime || Date.now(),
            messageCount: session.messages?.length || 0,
            isActive: this.chatStore.activeChat === session,
            // 🔧 为终端收件箱会话添加额外标识
            isTerminalInbox: Boolean(session.isTerminalInbox),
            receivingTerminal: session.receivingTerminal,
            // 🔧 添加特殊标识帮助VSCode端识别
            sessionType: session.isTerminalInbox ? 'terminal-inbox' : 'normal-chat'
          };

          const sessionType = session.isTerminalInbox ? '终端收件箱' : '普通IM';
          console.log(`🔧 [InboxSession] 准备同步${sessionType}会话${index}:`, syncSession.showName, 'sessionType:', syncSession.sessionType);
          humanSessions.push(syncSession);
        });

        // 🚫 不再同步AI会话，避免重复
        // AI会话由UnifiedSessionService直接从React侧获取，避免重复和类型混乱
        console.log(`📊 [InboxSession] 只同步${humanSessions.length}个人类会话到统一服务`);
        console.log(`🔧 [InboxSession] AI会话数量: ${this.filteredAiSessions.length}个（不同步，由UnifiedSessionService直接管理）`);

        // 调用统一服务的同步方法
        unifiedSessionService.syncVueSessionsFromWebView(humanSessions);
        console.log(`✅ [InboxSession] 同步${humanSessions.length}个人类会话到统一服务完成`);

      } catch (error) {
        console.error('❌ [InboxSession] 同步会话到统一服务失败:', error);
        console.error('❌ [InboxSession] 错误堆栈:', error.stack);
      }
    },

    // 新增：单个会话增量同步方法
    syncSingleSessionToUnified(session, unifiedSessionId) {
      try {
        console.log('🔧 [InboxSession] 增量同步单个会话:', session.showName);

        if (!window.vscodeServices) {
          console.log('⚠️ [InboxSession] VSCode环境不可用');
          return;
        }

        const unifiedSessionService = window.vscodeServices.get('IUnifiedSessionService');
        if (!unifiedSessionService || !unifiedSessionService.syncVueSessionsFromWebView) {
          console.log('⚠️ [InboxSession] UnifiedSessionService不可用');
          return;
        }

        // 只同步当前会话
        const singleSession = {
          id: session.targetId || session.id,
          sessionId: session.targetId || session.id,
          showName: session.showName || session.name || '未知会话',
          // 🔧 修复：统一使用vue-im类型，通过ID前缀和额外字段区分
          type: 'vue-im',
          lastSendTime: session.lastSendTime || session.lastMessageTime || Date.now(),
          lastMessageTime: session.lastSendTime || session.lastMessageTime || Date.now(),
          messageCount: session.messages?.length || 0,
          isActive: true, // 即将激活的会话
          // 🔧 为终端收件箱会话添加额外标识
          isTerminalInbox: Boolean(session.isTerminalInbox),
          receivingTerminal: session.receivingTerminal,
          // 🔧 添加特殊标识帮助VSCode端识别
          sessionType: session.isTerminalInbox ? 'terminal-inbox' : 'normal-chat'
        };

        const sessionType = session.isTerminalInbox ? '终端收件箱' : '普通IM';
        console.log(`🔧 [InboxSession] 同步单个${sessionType}会话:`, singleSession.showName);
        unifiedSessionService.syncVueSessionsFromWebView([singleSession]);
        console.log(`✅ [InboxSession] 单个${sessionType}会话同步完成`);

      } catch (error) {
        console.error('❌ [InboxSession] 单个会话同步失败:', error);
      }
    },

    // 清理事件监听器
    // 设置RooCode任务监听器
    setupRooCodeTaskListener() {
      console.log("[InboxSession] 设置RooCode任务监听器");
      
      if (window.vscodeServices) {
        const rooCodeService = window.vscodeServices.get('IRooCodeTaskHistoryService');
        if (rooCodeService && rooCodeService.onTaskHistoryChanged) {
          // 监听任务历史变更事件
          this.rooCodeTaskListener = rooCodeService.onTaskHistoryChanged((data) => {
            console.log("[InboxSession] RooCode任务历史变更:", data);
            this.handleRooCodeTaskUpdate(data);
          });
          
          // 设置定时刷新（每30秒）
/*           this.rooCodeRefreshTimer = setInterval(async () => {
            try {
              console.log("[InboxSession] 定时刷新RooCode任务列表");
              const taskData = await rooCodeService.getTaskHistory();
              this.handleRooCodeTaskUpdate(taskData);
            } catch (error) {
              console.error("[InboxSession] 定时刷新RooCode任务失败:", error);
            }
          }, 30000); */
        }
      }
    },
    
    // 清理RooCode任务监听器
    cleanupRooCodeTaskListener() {
      console.log("[InboxSession] 清理RooCode任务监听器");
      
      if (this.rooCodeTaskListener) {
        this.rooCodeTaskListener.dispose();
        this.rooCodeTaskListener = null;
      }
      
      if (this.rooCodeRefreshTimer) {
        clearInterval(this.rooCodeRefreshTimer);
        this.rooCodeRefreshTimer = null;
      }
    },
    
    // 处理RooCode任务更新
    async handleRooCodeTaskUpdate(taskData) {
      if (!taskData || !taskData.tasks) {
        console.log("[InboxSession] 无效的任务数据:", taskData);
        return;
      }
      
      console.log("[InboxSession] 处理RooCode任务更新，任务数:", taskData.tasks.length);
      
      // 保存非RooCode的AI会话
      const nonRooCodeSessions = this.aiSessions.filter(session => !session.isRooCodeTask);
      console.log("[InboxSession] 保留非RooCode会话数:", nonRooCodeSessions.length);
      
      // 转换并添加新的任务
      if (window.vscodeServices) {
        const rooCodeService = window.vscodeServices.get('IRooCodeTaskHistoryService');
        if (rooCodeService && rooCodeService.convertToAISession) {
          const rooCodeTasks = taskData.tasks.map(task => {
            const taskSession = rooCodeService.convertToAISession(task);
            return {
              ...taskSession,
              isRooCodeTask: true,
              originalTask: task
            };
          });
          
          console.log("[InboxSession] 转换后的RooCode任务数:", rooCodeTasks.length);
          
          // 合并所有会话并按时间排序（最新的在前）
          const allSessions = [...rooCodeTasks, ...nonRooCodeSessions];
          const sortedSessions = allSessions.sort((a, b) => {
            const timeA = a.lastMessageTime || a.lastSendTime || 0;
            const timeB = b.lastMessageTime || b.lastSendTime || 0;
            return timeB - timeA;  // 降序排序
          });
          
          // 使用 Vue.set 来确保响应式更新
          this.$set(this, 'aiSessions', sortedSessions);
          
          console.log("[InboxSession] RooCode任务已更新，当前会话总数:", this.aiSessions.length);
          
          // 如果有激活的任务，更新激活状态
          if (taskData.activeTaskId) {
            const activeSession = this.aiSessions.find(
              s => s.isRooCodeTask && s.originalTask?.id === taskData.activeTaskId
            );
            if (activeSession) {
              this.activeAISession = activeSession;
              console.log("[InboxSession] 更新激活任务:", taskData.activeTaskId);
            }
          }
          
          // 使用 Vue.nextTick 确保 DOM 更新
          this.$nextTick(() => {
            console.log("[InboxSession] DOM更新完成");
            // 再次强制更新以确保生效
            this.$forceUpdate();
          });
        } else {
          console.error("[InboxSession] RooCodeService未找到或无convertToAISession方法");
        }
      } else {
        console.error("[InboxSession] window.vscodeServices未定义");
      }
    },

    cleanupEventListeners() {
      console.log("[InboxSession] 清理事件监听器");

      try {
        // 🔧 清理防抖计时器
        if (this.activationDebounceTimer) {
          clearTimeout(this.activationDebounceTimer);
          this.activationDebounceTimer = null;
          console.log("[InboxSession] 激活防抖计时器已清理");
        }

        // 🔧 重置激活状态
        this.isActivatingSession = false;
        this.lastActivatedSessionId = null;

        // 移除DOM事件监听器
        window.removeEventListener('unified-session-created', this.handleSessionCreated);

        // 清理统一会话服务的事件订阅
        if (this.sessionEventUnsubscribe && typeof this.sessionEventUnsubscribe === 'function') {
          this.sessionEventUnsubscribe();
          this.sessionEventUnsubscribe = null;
          console.log("[InboxSession] 统一会话事件监听器已清理");
        }

        console.log("[InboxSession] 事件监听器清理完成");
      } catch (error) {
        console.error("[InboxSession] 清理事件监听器失败:", error);
      }
    },

    // ✅ 强制重新加载所有数据（用于 keep-alive 激活后的数据恢复）
    async forceReloadAllData() {
      console.log("[InboxSession] 强制重新加载所有数据开始");

      try {
        // 1. 检查和修复 store 状态
        await this.checkAndFixStoreState();

        // 2. 重新完整初始化
        await this.initializeComponent();

        console.log("[InboxSession] 强制重新加载完成");
      } catch (error) {
        console.error("[InboxSession] 强制重新加载失败:", error);
      }
    },

    // ✅ 轻量级数据刷新（用于已有数据的情况）
    async refreshData() {
      console.log("[InboxSession] 🔄 轻量级数据刷新开始");

      try {
        // 检查 store 状态
        await this.checkAndFixStoreState();

        // 只重新加载数据，不重新设置监听器
        await this.loadAllSessions();

        console.log("[InboxSession] 🔄 轻量级数据刷新完成");
      } catch (error) {
        console.error("[InboxSession] 🔄 轻量级数据刷新失败:", error);
      }
    },

    // ✅ 检查和修复 store 状态
    async checkAndFixStoreState() {
      console.log("[InboxSession] 🔍 检查 store 状态开始");

      // 详细检查 store 状态
      console.log("[InboxSession] 🔍 this.$store 存在:", !!this.$store);
      console.log("[InboxSession] 🔍 this.$store.state 存在:", !!this.$store?.state);
      console.log("[InboxSession] 🔍 chatStore 存在:", !!this.$store?.state?.chatStore);
      console.log("[InboxSession] 🔍 chatStore.chats 存在:", !!this.$store?.state?.chatStore?.chats);

      if (!this.$store) {
        console.error("[InboxSession] ❌ this.$store 不存在！这是严重问题");
        return false;
      }

      if (!this.$store.state) {
        console.error("[InboxSession] ❌ this.$store.state 不存在！");
        return false;
      }

      if (!this.$store.state.chatStore) {
        console.error("[InboxSession] ❌ chatStore 模块不存在！");
        console.log("[InboxSession] 🔍 可用的 store 模块:", Object.keys(this.$store.state));
        return false;
      }

      if (!this.$store.state.chatStore.chats) {
        console.warn("[InboxSession] ⚠️ chatStore.chats 不存在，尝试初始化");

        // 尝试调度初始化 action
        try {
          await this.$store.dispatch('initializeChatStore');
        } catch (error) {
          console.warn("[InboxSession] 初始化 chatStore 失败，尝试手动创建:", error);

          // 手动初始化 chatStore.chats
          if (this.$store.state.chatStore && !this.$store.state.chatStore.chats) {
            this.$store.state.chatStore.chats = [];
            console.log("[InboxSession] ✅ 手动创建了 chatStore.chats 数组");
          }
        }
      }

      // 最终验证
      const finalCheck = !!this.$store?.state?.chatStore?.chats;
      console.log("[InboxSession] 🔍 最终 store 状态检查:", finalCheck ? "✅ 正常" : "❌ 异常");

      return finalCheck;
    },

    isHumanChatActive(chat) {
      // 🔧 使用唯一标识符判断会话是否激活，而不是对象引用
      const activeChat = this.chatStore.activeChat;
      if (!activeChat || !chat) {
        return false;
      }

      // 通过多个字段进行匹配，确保判断准确
      const targetIdMatch = (activeChat.targetId || activeChat.id) === (chat.targetId || chat.id);
      const nameMatch = activeChat.showName === chat.showName;
      const typeMatch = activeChat.type === chat.type;

      // 对于终端收件箱会话，需要额外检查终端字段
      if (chat.isTerminalInbox && activeChat.isTerminalInbox) {
        const terminalMatch = activeChat.receivingTerminal === chat.receivingTerminal;
        return targetIdMatch && nameMatch && terminalMatch;
      }

      // 对于普通会话
      return targetIdMatch && nameMatch && typeMatch;
    },

    // 统一会话激活 - 终端会话和普通会话使用相同的激活流程
    async onActiveTerminalChatItem(chat, index) {
      console.log('🔧 [InboxSession] 激活终端会话 - 原始索引:', index, '会话:', chat.showName);

      // 🔧 验证索引的正确性
      const actualChat = this.chatStore.chats[index];
      if (actualChat?.targetId !== chat.targetId) {
        console.warn('🔧 [InboxSession] ⚠️ 索引验证失败！传入索引对应的会话不匹配');
        console.log('🔧 [InboxSession] 期望会话:', chat.showName, chat.targetId);
        console.log('🔧 [InboxSession] 实际会话:', actualChat?.showName, actualChat?.targetId);

        // 重新查找正确的索引
        const correctIndex = this.chatStore.chats.findIndex(c =>
          (c.targetId || c.id) === (chat.targetId || chat.id) &&
          ((c.isTerminalChat && c.senderTerminal === chat.senderTerminal) ||
            (c.isTerminalInbox && c.receivingTerminal === chat.receivingTerminal))
        );

        if (correctIndex === -1) {
          console.error('🔧 [InboxSession] ❌ 无法找到正确的会话索引');
          return;
        }

        console.log('🔧 [InboxSession] 🔍 重新找到正确索引:', correctIndex);
        index = correctIndex;
      }

      // 统一处理：直接激活会话（与普通会话相同）
      this.chatStore.activeChat = this.chatStore.chats[index];
      console.log('🔧 [InboxSession] ✅ 终端会话激活完成:', chat.showName, '消息数:', this.chatStore.chats[index]?.messages?.length || 0);

      // 🔧 核心修复：确保编辑器面板打开（与IM会话相同的逻辑）
      if (window.vscodeServices) {
        try {
          // 使用统一服务访问机制获取面板管理服务
          const panelManagerService = window.vscodeServices.get('panelManagerService');
          if (panelManagerService && panelManagerService.openChatEditor) {
            const chatUser = {
              id: chat.targetId || chat.id,
              nickName: chat.showName || chat.name,
              name: chat.showName || chat.name,
              headImage: chat.headImage || chat.avatar,
              headImageThumb: chat.headImageThumb || chat.avatar,
              avatar: chat.headImage || chat.avatar,
              // 标识这是终端会话
              isTerminalChat: chat.isTerminalChat,
              isTerminalInbox: chat.isTerminalInbox,
              receivingTerminal: chat.receivingTerminal,
              senderTerminal: chat.senderTerminal
            };
            panelManagerService.openChatEditor(chatUser);
            console.log("[InboxSession] ✅ 终端会话编辑器面板已打开:", chat.showName);
          } else {
            console.warn("[InboxSession] ⚠️ PanelManagerService或openChatEditor方法不可用");
          }
        } catch (error) {
          console.error("[InboxSession] ❌ 打开终端会话编辑器面板失败:", error);
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.chat-page {
  height: 100%;
  background: #f8f9fa;

  .search-container {
    padding: 12px 16px;
    background: white;
    border-bottom: 1px solid #e9ecef;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

    .search-input {
      width: 100%;

      ::v-deep .el-input__inner {
        border-radius: 20px;
        background: #f8f9fa;
        border: 1px solid #e9ecef;

        &:focus {
          background: white;
          border-color: #007bff;
        }
      }
    }
  }

  .chat-list-container {
    padding: 0;
    overflow-y: auto;

    .chat-group {
      .group-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 12px 16px;
        background: #f8f9fa;
        border-bottom: 1px solid #e9ecef;
        position: sticky;
        top: 0;
        z-index: 1;
        height: 44px; // 强制设置固定高度
        box-sizing: border-box;

        .header-left {
          display: flex;
          align-items: center;
          flex: 1;
          height: 20px; // 固定高度
          
          .collapse-icon, .spacer-icon {
            font-size: 12px;
            color: #6c757d;
            margin-right: 8px;
            width: 12px;
            height: 12px;
            display: inline-block;
            flex-shrink: 0; // 防止收缩
          }
          
          .collapse-icon {
            transition: transform 0.2s ease;
            
            &.expanded {
              transform: rotate(90deg);
            }
          }
          
          .spacer-icon {
            // 空占位符，保持与折叠图标相同的空间
          }
          
          .group-title {
            font-size: 14px;
            font-weight: 600;
            color: #495057;
            line-height: 20px; // 确保一致的行高
            height: 20px; // 固定高度
            display: flex;
            align-items: center;
            flex-shrink: 0;
          }
        }
        
        .group-count {
          font-size: 12px;
          color: #6c757d;
          background: #e9ecef;
          padding: 2px 8px;
          border-radius: 10px;
          height: 20px; // 固定高度
          line-height: 16px; // 确保徽章内文字垂直居中
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          box-sizing: border-box;
        }
        
        &.collapsible-header {
          cursor: pointer;
          user-select: none;
          transition: background-color 0.2s ease;

          &:hover {
            background: #f1f3f4;
          }
        }

      }

      .chat-list {
        background: white;

        .loading-placeholder, .empty-placeholder {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
          text-align: center;
          color: #909399;

          i {
            font-size: 24px;
            margin-bottom: 12px;
            
            &.el-icon-loading {
              animation: spin 1s linear infinite;
            }
          }

          span {
            font-size: 14px;
            line-height: 1.5;
            margin-bottom: 8px;
          }
          
          .empty-hint {
            font-size: 12px;
            color: #c0c4cc;
            line-height: 1.4;
          }
        }

        .loading-placeholder {
          color: #409eff;
          
          i {
            color: #409eff;
          }
        }

        .ai-chat-item {
          position: relative;
          border-bottom: 1px solid #f1f3f4;
          transition: all 0.2s ease;

          &:hover {
            background: #f8f9fa;
          }

          &.active {
            background: #e3f2fd;
            border-left: 3px solid #007bff;
          }

          &.has-complex-tools {
            &::before {
              content: "";
              position: absolute;
              left: 0;
              top: 0;
              bottom: 0;
              width: 2px;
              background: #ffc107;
            }
          }

          &.thinking {
            background: linear-gradient(90deg,
                rgba(255, 193, 7, 0.1) 0%,
                rgba(255, 193, 7, 0.05) 50%,
                rgba(255, 193, 7, 0.1) 100%);
            animation: thinking-glow 2s ease-in-out infinite;
          }
          
          &.roocode-task {
            background: linear-gradient(to right, rgba(139, 92, 246, 0.05), rgba(139, 92, 246, 0.02));
            border-left: 2px solid #8b5cf6;
            
            &:hover {
              background: linear-gradient(to right, rgba(139, 92, 246, 0.1), rgba(139, 92, 246, 0.05));
            }
            
            &.active {
              background: linear-gradient(to right, rgba(139, 92, 246, 0.15), rgba(139, 92, 246, 0.1));
              border-left: 3px solid #8b5cf6;
            }
          }

          .ai-indicator {
            display: flex;
            align-items: center;
            gap: 8px;

            .ai-avatar {
              font-size: 20px;
              width: 32px;
              height: 32px;
              display: flex;
              align-items: center;
              justify-content: center;
              border-radius: 50%;
              background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
              color: white;
              font-size: 14px;
            }

            .thinking-dots {
              display: flex;
              gap: 2px;

              span {
                width: 4px;
                height: 4px;
                background: #ffc107;
                border-radius: 50%;
                animation: thinking-dot 1.4s ease-in-out infinite both;

                &:nth-child(1) {
                  animation-delay: -0.32s;
                }

                &:nth-child(2) {
                  animation-delay: -0.16s;
                }

                &:nth-child(3) {
                  animation-delay: 0s;
                }
              }
            }
          }

          .ai-meta {
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            gap: 4px;

            .model-tag {
              font-size: 10px;
              background: #e3f2fd;
              color: #1976d2;
              padding: 2px 6px;
              border-radius: 8px;
              font-weight: 500;
            }

            .tools-badge {
              font-size: 12px;
              color: #ff9500;
            }

            .message-count {
              font-size: 11px;
              color: #6c757d;
              background: #f1f3f4;
              padding: 2px 6px;
              border-radius: 8px;
            }
          }
        }

        .human-chat-item {
          border-bottom: 1px solid #f1f3f4;

          &:hover {
            background: #f8f9fa;
          }

          &.active {
            background: #e8f5e8;
            border-left: 3px solid #28a745;
          }
        }

        // 终端收件箱会话样式
        .terminal-inbox {
          border-left: 2px solid #6c5ce7;
          background: linear-gradient(90deg, rgba(108, 92, 231, 0.05) 0%, transparent 100%);

          &:hover {
            background: linear-gradient(90deg, rgba(108, 92, 231, 0.1) 0%, rgba(108, 92, 231, 0.05) 100%);
          }

          &.active {
            background: linear-gradient(90deg, rgba(108, 92, 231, 0.15) 0%, rgba(108, 92, 231, 0.08) 100%);
            border-left: 3px solid #6c5ce7;
          }

          .terminal-badge {
            position: absolute;
            top: 8px;
            right: 8px;
            background: #6c5ce7;
            color: white;
            padding: 2px 6px;
            border-radius: 8px;
            font-size: 10px;
            font-weight: 500;
          }
        }

        .normal-chat {
          // 普通聊天的样式保持不变
        }
      }
    }

    .empty-state {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 200px;

      .empty-content {
        text-align: center;

        .empty-icon {
          font-size: 48px;
          color: #dee2e6;
          margin-bottom: 16px;
        }

        h3 {
          margin: 0 0 8px 0;
          color: #495057;
          font-size: 16px;
          font-weight: 500;
        }

        p {
          margin: 0;
          color: #6c757d;
          font-size: 14px;
        }
      }
    }

  }

  .action-footer {
    background: white;
    border-top: 1px solid #e9ecef;
    padding: 8px 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;

    .action-buttons {
      display: flex;
      gap: 8px;

      .el-button {
        border-radius: 16px;
        font-size: 12px;
        padding: 6px 12px;

        &--primary {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border: none;

          &:hover {
            background: linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%);
          }
        }
      }
    }

    .sync-status {
      .sync-time {
        font-size: 11px;
        color: #6c757d;
      }
    }
  }
}

@keyframes thinking-glow {

  0%,
  100% {
    box-shadow: inset 0 0 0 0 rgba(255, 193, 7, 0.1);
  }

  50% {
    box-shadow: inset 0 0 20px 5px rgba(255, 193, 7, 0.2);
  }
}

@keyframes thinking-dot {

  0%,
  80%,
  100% {
    transform: scale(0);
  }

  40% {
    transform: scale(1);
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

// 覆盖Element UI的默认样式
::v-deep .el-main {
  padding: 0;
}

::v-deep .el-header {
  padding: 0;
}

::v-deep .el-footer {
  padding: 0;
}
</style>

<style lang="scss">
// 激活状态样式 - 使用全局样式确保生效
.chat-item.active {
  background-color: #E3F2FD !important;
  border-left: 3px solid #409EFF !important;
  padding-left: 7px !important;
  box-shadow: inset 0 0 8px rgba(64, 158, 255, 0.15) !important;
}
</style>
